import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  CurrentScopeFacade,
  ManagementService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import {
  CustomReport,
  RestrictAccess,
  Staffer,
} from '@principle-theorem/principle-core';
import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import {
  ICustomReportColumn,
  IManagementUser,
  RestrictAccessEntity,
  type ICustomReport,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportTemplate,
  ICustomReportTemplate,
  REPORT_BUILDER_DATA_SOURCES,
  resolveDataSource,
} from '@principle-theorem/reporting';
import {
  DocumentReference,
  Firestore,
  multiSortBy$,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { isEqual } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  IReportBuilderColumnsDialogRequest,
  IReportBuilderColumnsDialogResponse,
  ReportBuilderColumnsDialogComponent,
} from '../report-builder/report-builder-results-table/report-builder-columns-dialog/report-builder-columns-dialog.component';
import { ReportEditorService } from '../report-builder/report-editor.service';
import {
  ISelectReportTemplateDialogResponse,
  SelectReportTemplateDialogComponent,
} from './custom-report/select-report-template-dialog/select-report-template-dialog.component';

@Injectable()
export class CustomReportsService {
  private _scope = inject(CurrentScopeFacade);
  private _organisation = inject(OrganisationService);
  private _management = inject(ManagementService);
  private _dialog = inject(BasicDialogService);
  private _router = inject(Router);
  private _reportEditor = inject(ReportEditorService);

  async createCustomReport(): Promise<
    DocumentReference<ICustomReport> | undefined
  > {
    const brand = await snapshot(this._scope.currentBrand$);
    const staffer = await snapshot(this._organisation.staffer$);
    if (!brand || !staffer) {
      return;
    }
    const response = await this.openSelectReportTemplateDialog();
    if (!response) {
      return;
    }
    return CustomReportTemplate.createReportFromTemplate(
      brand.ref,
      response.template,
      staffer
    );
  }

  async openSelectReportTemplateDialog(): Promise<
    ISelectReportTemplateDialogResponse | undefined
  > {
    return this._dialog
      .open<
        SelectReportTemplateDialogComponent,
        void,
        ISelectReportTemplateDialogResponse
      >(
        SelectReportTemplateDialogComponent,
        DialogPresets.extraLarge({
          minHeight: '90vh',
          maxHeight: '90vh',
        })
      )
      .afterClosed()
      .toPromise();
  }

  getAvailableReports$(): Observable<WithRef<ICustomReport>[]> {
    return combineLatest([this.getAllReports$(), this.getPermissions$()]).pipe(
      map(([reports, permissions]) => this._filterReports(reports, permissions))
    );
  }

  getAllReports$(): Observable<WithRef<ICustomReport>[]> {
    return this._scope.currentBrand$.pipe(
      switchMap((brand) => (brand ? CustomReport.all$(brand) : of([]))),
      multiSortBy$((report) => report.name)
    );
  }

  getPermissions$(): Observable<ICustomReportPermissions> {
    return combineLatest([
      this._organisation.staffer$,
      this._management.user$,
    ]).pipe(
      switchMap(([staffer, managementUser]) =>
        this._getCustomReportPermissions$(staffer, managementUser)
      )
    );
  }

  async editCustomReport(
    customReport: WithRef<ICustomReport>
  ): Promise<WithRef<ICustomReport> | undefined> {
    const response = await this._reportEditor.openCustomReportDialog({
      customReport,
    });
    if (!response) {
      return;
    }

    if (response.changeTemplate) {
      const templateResponse = await this.openSelectReportTemplateDialog();
      if (!templateResponse) {
        return;
      }
      const confirmed = await this._confirmDataSourceChange();
      if (!confirmed) {
        return;
      }
      return this._applyTemplateToCustomReport(
        customReport,
        templateResponse.template
      );
    }
    if (response.deleteCustomForm) {
      await this.deleteCustomReport(customReport);
      return;
    }
    if (response.formData) {
      await Firestore.patchDoc(customReport.ref, response.formData);
      return Firestore.getDoc(customReport.ref);
    }
  }

  async deleteCustomReport(
    customReport: WithRef<ICustomReport>
  ): Promise<void> {
    const isConfirmed = await this._dialog.confirm({
      title: 'Delete Custom Report',
      prompt: `Are you sure you want to delete the custom report "${customReport.name}"?`,
      submitLabel: 'Yes, Delete',
      submitColor: 'warn',
    });
    if (!isConfirmed) {
      return;
    }
    await Firestore.deleteDoc(customReport.ref);
    await this._router.navigate(['reporting', 'custom-reports']);
  }

  // async editDatasource(customReport: WithRef<ICustomReport>): Promise<boolean> {
  //   const editResponse = await this._openEditDataSourceDialog(customReport);
  //
  //   if (editResponse?.selectFromTemplate) {
  //     const templateResponse = await this.openSelectReportTemplateDialog();
  //     if (!templateResponse) {
  //       return false;
  //     }
  //     const confirmed = await this._confirmDataSourceChange();
  //     if (!confirmed) {
  //       return false;
  //     }
  //     await this._applyTemplateToCustomReport(
  //       customReport,
  //       templateResponse.template
  //     );
  //     return true;
  //   }
  //
  //   if (editResponse?.formData) {
  //     if (customReport.dataSource !== editResponse.formData.dataSource) {
  //       const confirmed = await this._confirmDataSourceChange();
  //       if (!confirmed) {
  //         return false;
  //       }
  //     }
  //     await this._applyDataSourceChange(customReport, editResponse.formData);
  //     return true;
  //   }
  //
  //   return false;
  // }

  async editTableColumns(
    customReport: WithRef<ICustomReport>
  ): Promise<ICustomReportColumn[] | undefined> {
    const dataSource = resolveDataSource(
      customReport?.dataSource,
      REPORT_BUILDER_DATA_SOURCES
    );
    if (!dataSource) {
      return;
    }
    const data: IReportBuilderColumnsDialogRequest = {
      dataSource,
      columns: customReport?.columns ?? [],
    };

    const response = await this._dialog
      .open<
        ReportBuilderColumnsDialogComponent,
        IReportBuilderColumnsDialogRequest,
        IReportBuilderColumnsDialogResponse
      >(
        ReportBuilderColumnsDialogComponent,
        DialogPresets.large({ maxHeight: '80vh', data })
      )
      .afterClosed()
      .toPromise();

    if (!response || isEqual(customReport.columns, response.columns)) {
      return;
    }
    await Firestore.patchDoc(customReport.ref, { columns: response.columns });
    return response.columns;
  }

  private _getCustomReportPermissions$(
    staffer?: WithRef<IStaffer>,
    managementUser?: IManagementUser
  ): Observable<ICustomReportPermissions> {
    const canEdit$ = this._canEditCustomReports$(staffer, managementUser);
    const accessEntities$ = staffer
      ? RestrictAccess.getAccessEntities$(staffer)
      : of([]);
    return combineLatest([canEdit$, accessEntities$]).pipe(
      map(([canEdit, accessEntities]) => ({ canEdit, accessEntities }))
    );
  }

  private _canEditCustomReports$(
    staffer?: WithRef<IStaffer>,
    managementUser?: IManagementUser
  ): Observable<boolean> {
    if (managementUser) {
      return of(true);
    }
    if (!staffer) {
      return of(false);
    }
    return Staffer.hasPermission$(
      staffer,
      ReportingPermissions.ReportingCustomReportEdit
    );
  }

  private _filterReports(
    reports: WithRef<ICustomReport>[],
    permissions: ICustomReportPermissions
  ): WithRef<ICustomReport>[] {
    return reports.filter(
      (report) =>
        permissions.canEdit ||
        RestrictAccess.hasAccess(
          report.restrictAccessTo,
          permissions.accessEntities
        )
    );
  }

  private _confirmDataSourceChange(): Promise<boolean | undefined> {
    return this._dialog.confirm({
      title: 'Change Data Source',
      prompt: `Are you sure you want to update the data source for this report? This will reset parts of the Report.`,
      submitLabel: 'Change Data Source',
      submitColor: 'warn',
      cancelLabel: 'Cancel',
    });
  }

  private async _applyTemplateToCustomReport(
    customReport: WithRef<ICustomReport>,
    template: ICustomReportTemplate
  ): Promise<WithRef<ICustomReport>> {
    await Firestore.patchDoc(customReport.ref, {
      name: CustomReportTemplate.getTemplateName(template),
      description: template.description,
      dataSource: template.dataSourceId,
      queryScopes: customReport.queryScopes,
      staticFilters: template.staticFilters ?? [],
      chartSections: template.chartSections ?? [],
      columns: template.columns ?? [],
    });
    return Firestore.getDoc(customReport.ref);
  }
}

interface ICustomReportPermissions {
  canEdit: boolean;
  accessEntities: RestrictAccessEntity[];
}
